<template>
    <transition name="fade">
        <div
            v-if="activeAuthOverlay"
            class="z-60"
        >
            <div class="fixed top-0 bottom-0 left-0 right-0 z-30 w-screen h-full overlay-container"></div>

            <div class="fixed left-0 top-0 z-30">
                <div class="relative w-screen h-screen overflow-y-auto">
                    <div class="relative max-w-6xl mx-auto">
                        <div class="absolute right-0">
                            <i
                                class="mt-4 mr-4 cursor-pointer md:mt-8 md:mr-8 icon icon-close text-black md:text-white w-6 h-6 md:w-8 md:h-8"
                                @click="closeOverlay"
                            ></i>
                        </div>
                    </div>

                    <div class="max-w-6xl min-h-screen mx-auto md:flex">
                        <div class="flex-col items-center justify-center w-full md:flex lg:flex-row lg:m-2 lg:pt-8">
                            <div class="pt-16 pb-6 md:pb-10 heading-wrapper lg:pt-0 lg:pb-0 lg:w-1/2 max-md:container">
                                <div class="text-center lg:p-4 md:text-white">
                                    <div v-if="activeAuthOverlay === 'login'">
                                        <h1 class="font-display-3 md:font-display-2 lg:font-display-1">
                                            {{ t('welcomeBack') }}
                                        </h1>
                                    </div>

                                    <RegistrationHeader
                                        v-if="activeAuthOverlay === 'register'"
                                        :treatment="registrationTreatment"
                                    />
                                </div>
                            </div>
                            <div class="w-full md:w-auto lg:w-1/2 lg:p-4">
                                <AuthForm
                                    :type="activeAuthOverlay"
                                    class="w-screen max-w-md mx-auto p-7 md:p-8 bg-white md:rounded-lg"
                                    @update:type="$store.commit('ui/SET_ACTIVE_AUTH_OVERLAY', $event)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AuthForm from '@components/AuthForm';
import RegistrationHeader from '@components/RegistrationHeader';
import { isEmpty } from 'lodash-es';
import route from '@router/route';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'TheAuthOverlay',
    components: {
        RegistrationHeader,
        AuthForm,
    },
    setup() {
        const { t } = useTranslation('authentication');
        return { t };
    },
    data() {
        return {
            isUniPageContext: false,
            registrationTreatment: 'general',
            routesWithUniversityContext: [
                'university.show',
                'course.show',
                'document.show',
                'flashcards.slug.id',
                'courses.posts.question',
                'documents.posts.question',
            ],
        };
    },
    computed: {
        ...mapGetters('auth', ['authDetails']),
        ...mapGetters('ui', ['activeAuthOverlay']),
        ...mapGetters('university', ['currentUniversity', 'universityStats']),
        authTrigger() {
            return this.authDetails.extras.authTrigger || null;
        },
        isTopUni() {
            return this.currentUniversity.isTop || this.currentUniversity.top;
        },
    },
    watch: {
        activeAuthOverlay(newValue) {
            if (newValue) {
                if (this.currentUniversity.id !== this.universityStats.id) {
                    this.clearUniStats();
                }
                this.setAuthOverlayTreatment();
            }
        },
        authTrigger() {
            this.setAuthOverlayTreatment();
        },
    },
    mounted() {
        this.setPageContext();
        this.setAuthOverlayTreatment();
    },
    methods: {
        ...mapActions('ui', ['closeAuthOverlay', 'setScrollingEnabled']),
        /**
         * Setting the page context within with authenticatino was triggered
         * This is for design treatments but can be extended useful to pass the information needed for post-registration redirection to the registration process
         * * */
        setPageContext() {
            this.isUniPageContext = this.routesWithUniversityContext.some(
                (currentRoute) => route().current() === currentRoute,
            );
        },
        /**
         * method for setting the design of the auth overlay
         * The treatment depends on the page context and from which button the overlay was opened
         * * */
        setAuthOverlayTreatment() {
            let treatment = 'general';
            // first set the treatment according to the page context - this is the default for the page
            if (this.isUniPageContext && this.currentUniversity && !isEmpty(this.currentUniversity)) {
                treatment = this.isTopUni ? 'topUni' : 'normalUni';
            }
            // then override if there was a specific trigger with a different treatment to its page context
            if (this.authTrigger) {
                if (this.authTrigger === 'download') {
                    treatment = 'download';
                } else if (
                    this.authTrigger === 'homepageUniSearch' &&
                    this.currentUniversity &&
                    !isEmpty(this.currentUniversity)
                ) {
                    treatment = this.isTopUni ? 'topUni' : 'normalUni';
                }
            }
            this.registrationTreatment = treatment;
            this.setAuthRequestUniversity();
            if (
                this.registrationTreatment === 'topUni' &&
                (isEmpty(this.universityStats) || this.universityStats.id !== this.currentUniversity.id)
            ) {
                this.getUniStats();
            }
        },
        closeOverlay() {
            this.closeAuthOverlay();
        },
        getUniStats() {
            this.$store.dispatch('university/retrieveStats', this.currentUniversity.id).then(() => {
                if (isEmpty(this.universityStats)) {
                    // edge that that no stats are stored in cache - show standard treatment ratehr than zeros
                    this.registrationTreatment = 'normalUni';
                }
            });
        },
        clearUniStats() {
            this.$store.dispatch('university/clearStats');
        },
        setAuthRequestUniversity() {
            let sdUni = null;
            if (this.registrationTreatment !== 'general') {
                sdUni = this.currentUniversity.id;
            }

            this.$store.dispatch('auth/setAuthDetailsExtras', {
                ...this.authDetails.extras,
                sduni: sdUni,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.heading-wrapper {
    background-image: url(/images/backgrounds/background-header-blue.svg);
    background-repeat: no-repeat;
    background-size: cover;

    @screen md {
        background-image: none;
    }
}

.overlay-container {
    &::before {
        content: '';
        background-color: white;
        opacity: 1;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        position: absolute;
        z-index: -1;
    }

    @screen md {
        &::after {
            content: '';
            background-image: url(/images/backgrounds/background-black.svg);
            opacity: 0.9;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            position: absolute;
            z-index: -2;
            background-repeat: no-repeat;
            background-size: cover;
        }

        &::before {
            background-color: black;
            opacity: 0.6;
        }
    }
}
</style>
